import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FlaskConical, Atom, LineChart, Shield, Microscope, ClipboardCheck, Beaker, Sparkles } from 'lucide-react';
import { rdImages } from '../data/mockData';

const RDCapabilities = () => {
  const capabilities = [
    {
      icon: FlaskConical,
      title: "Custom Formulation Development",
      description: "End-to-end formulation design tailored to your brand's unique positioning and target market.",
      features: [
        "Bespoke formula creation from concept to commercial scale",
        "Texture and sensory profile optimization",
        "Active ingredient selection and synergy studies",
        "Claim substantiation support"
      ]
    },
    {
      icon: Atom,
      title: "Active Ingredient Innovation",
      description: "Cutting-edge ingredient sourcing and compatibility evaluation for performance-driven products.",
      features: [
        "Novel ingredient identification and evaluation",
        "Ingredient compatibility testing",
        "Bioavailability and penetration studies",
        "Natural and synthetic active integration"
      ]
    },
    {
      icon: LineChart,
      title: "Stability & Shelf-Life Studies",
      description: "Comprehensive stability protocols ensuring product integrity throughout its lifecycle.",
      features: [
        "Accelerated stability testing (ICH guidelines)",
        "Real-time stability monitoring",
        "Microbial challenge testing",
        "Packaging compatibility studies"
      ]
    },
    {
      icon: Microscope,
      title: "Performance Validation",
      description: "Rigorous testing methodologies to validate product efficacy and consumer claims.",
      features: [
        "In-vitro efficacy testing",
        "Clinical trial coordination",
        "Consumer perception studies",
        "SPF and PA testing for sun care"
      ]
    },
    {
      icon: ClipboardCheck,
      title: "Quality Control & Assurance",
      description: "Industry-leading QC protocols ensuring consistent, high-quality product output.",
      features: [
        "Raw material authentication",
        "In-process quality checks",
        "Final product specification testing",
        "Certificate of Analysis (CoA) generation"
      ]
    },
    {
      icon: Beaker,
      title: "Pilot Batch Development",
      description: "Small-scale production runs to validate formulations before commercial scale-up.",
      features: [
        "Laboratory to pilot scale transition",
        "Process optimization and documentation",
        "Batch-to-batch consistency validation",
        "Manufacturing troubleshooting support"
      ]
    },
    {
      icon: Shield,
      title: "Regulatory Compliance Support",
      description: "Navigate complex regulatory landscapes with expert guidance and documentation.",
      features: [
        "Ingredient safety assessments",
        "Label claim substantiation",
        "INCI nomenclature and declarations",
        "Market-specific compliance (India, EU, FDA)"
      ]
    },
    {
      icon: Sparkles,
      title: "Speed-to-Market Acceleration",
      description: "Streamlined development workflows that reduce time from concept to commercial launch.",
      features: [
        "Parallel development tracks",
        "Rapid prototyping capabilities",
        "Expedited testing protocols",
        "Dedicated project management"
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-block mb-6">
              <span className="text-sm font-semibold text-brand-teal uppercase tracking-wider">
                R&D Excellence
              </span>
            </div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight mb-6">
              Comprehensive <span className="font-medium text-brand-teal">Formulation Development</span> Services
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              State-of-the-art laboratory facilities and expert scientific teams dedicated to transforming 
              your product vision into commercial reality with uncompromising quality and speed.
            </p>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {capabilities.map((capability, index) => {
              const IconComponent = capability.icon;
              return (
                <div
                  key={index}
                  className="border border-border p-8 hover-lift cursor-pointer group"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-brand-teal/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-teal transition-colors duration-200">
                      <IconComponent className="text-brand-teal group-hover:text-white transition-colors duration-200" size={28} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-medium mb-3">{capability.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                        {capability.description}
                      </p>
                      <ul className="space-y-2">
                        {capability.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-brand-teal mt-1">•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Laboratory Images */}
      <section className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-light mb-4">
              Our <span className="font-medium text-brand-teal">Laboratory Facilities</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              State-of-the-art equipment and controlled environments for precise formulation development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={rdImages.testing}
                alt="Laboratory testing"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={rdImages.botanical}
                alt="Natural ingredients"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="aspect-[4/5] overflow-hidden">
              <img
                src={rdImages.quality}
                alt="Quality control"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto bg-foreground text-white p-12 lg:p-16">
            <div className="text-center">
              <h2 className="text-3xl lg:text-4xl font-light mb-6">
                Let's Develop Your Formulation
              </h2>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-2xl mx-auto">
                Schedule a consultation with our R&D team to discuss your product requirements, 
                timeline, and formulation objectives.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-foreground bg-white hover:bg-white/90"
                >
                  Talk to Our Scientists
                  <ArrowRight size={18} className="ml-2" />
                </Link>
                <Link
                  to="/process"
                  className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold text-white border-2 border-white hover:bg-white hover:text-foreground"
                >
                  See Our Process
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RDCapabilities;
